'use client';

import SharedFileProgressProvider from '@unifyapps/carbon/no-code/context/SharedFileProgressProvider';
import type { CustomBlockEntity } from '@unifyapps/defs/blocks/Custom/types';
import type { ThemeComponentStyle } from '@unifyapps/defs/theme/types';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { ColorScheme, InterfaceEntity } from '@unifyapps/defs/types/interface';
import type {
  SnackbarComponent,
  SnackbarConfig,
} from '@unifyapps/defs/types/interface/components/Snackbar/type';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import isNative from '@unifyapps/network/utils/native/isNative';
import { SnackbarProvider } from '@unifyapps/ui/components/Snackbar';
import { produce } from 'immer';
import _merge from 'lodash/merge';
import _set from 'lodash/set';
import type React from 'react';
import { useId, useMemo } from 'react';
import { dynamic } from '../../../../external/dynamic';
import { getDefaultThemeIdValue } from '../../../../theme/utils/themeConditionalUtils';
import type { WithPartial } from '../../../../types';
import { BlockAppearanceStylesProvider } from '../../../context/BlockAppearanceStylesProvider';
import { AppDependencyGraphContext } from '../../../context/DependencyGraphProvider';
import { DeviceCapabilitiesProvider } from '../../../context/DeviceCapabilitiesContext';
import type BlocksRegistry from '../../../context/RegistryProvider/BlocksRegistry';
import { useColorSchemeDetails } from '../../../hooks/useColorSchemeDetails';
import useGetInterfaceThemeById from '../../../hooks/useGetInterfaceThemeById';
import { useInitialiseCustomBlocks as useInitialiseBuilderCustomBlocks } from '../../../hooks/useInitialiseCustomBlocks';
import { useTranslatedInterfaceRecord } from '../../../hooks/useTranslatedInterfaceRecord';
import { requestPulseEnable } from '../../../pulse/runtime';
import type { WithRemountProps } from '../../../stores/app/GlobalAppStateStore';
import { GlobalAppStateStoreProvider } from '../../../stores/app/GlobalAppStateStore';
import {
  AppPulseScopeRegistrar,
  AppPulseScopeRegistrarPassthrough,
} from '../../../stores/app/GlobalAppStateStore/AppPulseScopeRegistrar';
import { AppDataSourceRecordStoreProvider } from '../../../stores/DataSourceRecordStore';
import { InterfaceClient, InterfaceModes } from '../../../stores/InterfaceStore';
import type { InterfaceBuilderStoreProviderProps } from '../../../stores/InterfaceStore/builder';
import { InterfaceAppStoreProvider as InterfaceAppStoreRunnerProvider } from '../../../stores/InterfaceStore/runner/InterfaceAppStoreProvider';
import { AppDataSourceController } from '../../DataSourceController/controllers/AppDataSourceController';
import { AppPageFunctionController } from '../../PageFunctionController';
import type { InterfaceResourceProviderRefType } from '../../page/InterfacePageProviders/types';
import InterfaceAppRunnerSideEffects from '../InterfaceAppRunnerSideEffects';
import AppDependencyGraphProvider from './context/AppDependencyGraphProvider';
import InterfaceAppActionProvider from './context/InterfaceAppActionProvider';
import useInterfaceAppProvidersInitialState from './hooks/useInterfaceAppProvidersInitialState';

const InterfaceAppStoreBuilderProvider = dynamic(() =>
  import(
    /* webpackChunkName: "InterfaceAppStoreBuilderProvider" */
    '../../../stores/InterfaceStore/builder/InterfaceAppStoreProvider'
  ).then((mod) => mod.InterfaceAppStoreProvider),
);

const InterfaceAppBuilderSideEffects = dynamic(() =>
  import(
    /* webpackChunkName: "InterfaceAppBuilderSideEffects" */
    '../InterfaceAppBuilderSideEffects'
  ).then((mod) => mod.default),
);

export type InterfaceAppProvidersProps = WithPartial<
  Omit<WithRemountProps, 'colorSchemeDetails'>,
  'client' | 'mode' | 'deviceDetails'
> & {
  customBlocks?: CustomBlockEntity[];
  children: React.ReactNode;
  interfaceRecord: InterfaceEntity;
  parentInterfaceRecord?: InterfaceEntity;
  registry: BlocksRegistry;
  dataSources?: Record<string, DataSourceEntity | undefined>;
  globalInterfacePage?: InterfacePageEntity;
  colorScheme?: ColorScheme;
  /** theme id from cookie/search param (builder + public); overrides interface default when set */
  interfaceThemeId?: string;
  appVariantKey?: string;
  pageVariablesInitialValue?: Record<string, unknown>;
  interfaceResourceProviderRef?: React.MutableRefObject<
    InterfaceResourceProviderRefType | undefined
  >;
  onInterfaceResourceProviderRefInit?: () => void;
  snackbarConfig?: SnackbarConfig;
};

function InterfaceAppProviders(props: InterfaceAppProvidersProps) {
  const {
    children,
    interfaceRecord: initialInterfaceRecord,
    permissions,
    parentInterfaceRecord,
    mode = InterfaceModes.RUNNER,
    client = mode === InterfaceModes.RUNNER
      ? InterfaceClient.UA_MATRIX
      : InterfaceClient.UA_PLATFORM,
    envVariables,
    locale,
    registry,
    basePath,
    commonNamespace,
    deviceDetails,
    // latest custom block entities
    customBlocks,
    globalInterfacePage,
    colorScheme,
    interfaceThemeId,
    appVariantKey,
    pageVariablesInitialValue,
    interfaceResourceProviderRef,
    onInterfaceResourceProviderRefInit,
    snackbarConfig,
  } = props;

  // ensure each InterfaceAppProviders mount has a unique SnackbarProvider id so sonner's
  // toasterId routing doesn't broadcast a single showSnackbar call to every sibling
  // Toaster sharing the same interfaceRecord.id (eg. copilot embeds the host interface
  // multiple times via ChatList / CopilotResponseMessage).
  const snackbarInstanceId = useId();

  const { interfaceRecord } = useTranslatedInterfaceRecord({
    interfaceRecord: initialInterfaceRecord,
    commonNamespace,
    /**
     * We don't want to translate the interface record in case of native because:
     * 1. We are already translating it in the AuthStackNavigator for navigation keys
     * 2. commonNamespace only contains navigation keys so we don't need to translate it again
     */
    shouldTranslate: !isNative(),
  });

  const colorSchemeDetails = useColorSchemeDetails({ colorScheme, interfaceRecord });

  const InterfaceAppStoreProvider =
    mode === InterfaceModes.RUNNER
      ? InterfaceAppStoreRunnerProvider
      : InterfaceAppStoreBuilderProvider;

  const InterfaceAppSideEffects =
    mode === InterfaceModes.BUILDER
      ? InterfaceAppBuilderSideEffects
      : InterfaceAppRunnerSideEffects;

  const FinalAppPulseScopeRegistrar =
    mode === InterfaceModes.RUNNER ? AppPulseScopeRegistrarPassthrough : AppPulseScopeRegistrar;

  const {
    dependencyGraphStoreRef,
    initialDataSourceRecordStoreState,
    interfaceStoreInitialValue,
    getDependencyGraphActions,
    interfaceStoreRef,
    globalStateStoreRef,
  } = useInterfaceAppProvidersInitialState({
    ...props,
    mode,
    interfaceResourceProviderRef,
    onInterfaceResourceProviderRefInit,
  });

  useInitialiseBuilderCustomBlocks(registry, customBlocks);

  // fetch theme entity to extract componentProps (e.g. snackbar position) and merge into the theme
  const defaultThemeId = getDefaultThemeIdValue(interfaceRecord.properties.theme?.defaultThemeId);
  const { themeEntity } = useGetInterfaceThemeById(defaultThemeId);

  const snackbarTheme = useMemo(() => {
    const baseTheme = interfaceRecord.properties.theme;
    // theme entity stores anchor/coordinates under componentProps.base (JSON); narrow for Interface['theme']
    const snackbarBase = (
      themeEntity?.properties?.components?.snackbar as ThemeComponentStyle | undefined
    )?.componentProps?.base as SnackbarComponent['base'] | undefined;
    if (!snackbarBase) return baseTheme;
    return produce(baseTheme, (draft) => {
      if (!draft) return;
      // _merge(undefined, x) doesn't assign to draft; ensure path exists and assign merged result
      _set(
        draft,
        'components.snackbar.base',
        _merge({}, draft.components?.snackbar?.base, snackbarBase),
      );
    });
  }, [interfaceRecord.properties.theme, themeEntity?.properties?.components?.snackbar]);

  const interfaceAppStoreProps = useMemo<InterfaceBuilderStoreProviderProps>(() => {
    return {
      interfaceStoreRef,
      // not needed in runner, but we are passing because we conditionally render the runner and builder component
      getDependencyGraphActions,
      value: interfaceStoreInitialValue,
    };
  }, [interfaceStoreRef, getDependencyGraphActions, interfaceStoreInitialValue]);

  if (mode !== InterfaceModes.RUNNER) requestPulseEnable();

  const content = (
    <DeviceCapabilitiesProvider>
      <SharedFileProgressProvider>
        <AppDataSourceRecordStoreProvider initialValue={initialDataSourceRecordStoreState}>
          <GlobalAppStateStoreProvider
            appVariantKey={appVariantKey}
            basePath={basePath}
            client={client}
            colorSchemeDetails={colorSchemeDetails}
            commonNamespace={commonNamespace}
            deviceDetails={deviceDetails}
            envVariables={envVariables}
            getDependencyGraphActions={getDependencyGraphActions}
            globalStateStoreRef={globalStateStoreRef}
            interfaceRecord={interfaceRecord}
            interfaceThemeId={interfaceThemeId}
            locale={locale}
            mode={mode}
            pageFunctions={globalInterfacePage?.properties.pageFunctions}
            pageVariables={globalInterfacePage?.properties.pageVariables}
            pageVariablesInitialValue={pageVariablesInitialValue}
            parentInterfaceRecord={parentInterfaceRecord}
            permissions={permissions}
            registry={registry}
          >
            <>
              <FinalAppPulseScopeRegistrar interfaceRecord={interfaceRecord} />
              <InterfaceAppStoreProvider {...interfaceAppStoreProps}>
                <AppDependencyGraphProvider
                  ContextComponent={AppDependencyGraphContext}
                  dependencyGraphStoreRef={dependencyGraphStoreRef}
                >
                  <InterfaceAppActionProvider>
                    <InterfaceAppSideEffects
                      initialColorSchemeDetails={colorSchemeDetails}
                      locale={locale}
                    >
                      <AppDataSourceController>
                        <AppPageFunctionController />
                        <BlockAppearanceStylesProvider>{children}</BlockAppearanceStylesProvider>
                      </AppDataSourceController>
                    </InterfaceAppSideEffects>
                  </InterfaceAppActionProvider>
                </AppDependencyGraphProvider>
              </InterfaceAppStoreProvider>
            </>
          </GlobalAppStateStoreProvider>
        </AppDataSourceRecordStoreProvider>
      </SharedFileProgressProvider>
    </DeviceCapabilitiesProvider>
  );

  // For native apps, skip the nested SnackbarProvider.
  // This ensures toasts use the root SnackbarProvider from AppNavigator and persist across page navigation.
  // The issue: In native, each public page gets its own InterfaceAppContainer, meaning each gets its own
  // SnackbarProvider. When navigating between pages, the old page's SnackbarProvider unmounts (with its toast),
  // and the new page's SnackbarProvider mounts.
  if (isNative()) {
    return content;
  }

  return (
    <SnackbarProvider
      config={snackbarConfig}
      id={`carbon-interface-app-providers-${interfaceRecord.id}-${mode}-${snackbarInstanceId}`}
      theme={snackbarTheme}
    >
      {content}
    </SnackbarProvider>
  );
}

export default InterfaceAppProviders;
